import { useMobileWidth } from '@utils/hooks';

import AttendeeInfo from '@composites/AttendeeInfo';
import CompanyInfo from '@composites/CompanyInfo';
import Sessions from '@composites/Sessions';
import HeroSection from '@composites/HeroSection';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getEventDetails } from '@store/slices/eventSlice';
import { useGetPublicSpeakerDetails } from '@utils/apiHooks';
import { adminEventsRouteFormat } from '@constants/paths';
import { SPEAKER_EMAIL } from '@utils/constants';
import { MetaTags } from '@components/MetaTags';
import { useTheme } from 'styled-components';
import { getImageSrc } from '@utils/fn';
import MailToButton from '@components/MailToButton';
import { SectionGrid, GridSpan } from './styles';
const SpeakerPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMobileWidth();
  const { id } = useParams();
  const sluggedEvent = useSelector(getEventDetails)?.[0];

  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const sourceEventId = searchParams.get('sourceEvent');
  const pathSegments = pathname.split('/').filter(Boolean);
  const secondLastSegment = pathSegments[pathSegments.length - 2];

  const subject = `${t('apply-as-a-speaker-request-subject', { eventName: sluggedEvent?.name })}`;
  const body = `${t('apply-as-a-speaker-request-body', { eventName: sluggedEvent?.name })}`;
  const mailtoLink = `mailto:${SPEAKER_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const eventId = sourceEventId || sluggedEvent?.id;

  const { data: speakerDetails, isLoading } = useGetPublicSpeakerDetails({
    speakerId: id ?? '',
    eventId,
  });
  // Redirect to 404 page if the speaker isn't found
  useEffect(() => {
    if (id && sluggedEvent?.slug && !speakerDetails?.data && !isLoading) {
      navigate(adminEventsRouteFormat(sluggedEvent.slug, ''));
    }
  }, [
    id,
    navigate,
    sluggedEvent?.slug,
    speakerDetails?.data,
    isLoading,
    eventId,
  ]);

  return (
    <>
      <MetaTags
        title={`${String(sluggedEvent?.name ?? '')} ${String(speakerDetails?.data?.first_name ?? '')} ${String(speakerDetails?.data?.last_name ?? '')}`}
        description={`${String(speakerDetails?.data?.sessions?.map((item: any) => item?.title).join(', ') ?? '')}`}
        keywords={`${String(sluggedEvent?.slug ?? '')}, ${String(secondLastSegment ?? '')}, ${String(speakerDetails?.data?.first_name ?? '')} ${String(speakerDetails?.data?.last_name ?? '')}`}
        image={getImageSrc(speakerDetails?.data?.company?.logo)}
        url={window.location.href}
        twitterCardType="summary"
        type="product"
      />
      <SectionGrid>
        <GridSpan span={12}>
          <HeroSection
            backgroundImage={getImageSrc(
              'https://media.we-conect.com/images/website/speaker_hero_bg.png',
              sluggedEvent?.portfolio?.slug
            )}
            darkOverlay
            title={t('best-in-class-speakers-will-share-their-expertise')}
            description={t('experience-top-experts-text')}
            buttonText={t('apply-as-speaker')}
            buttonURL={''}
            button={
              <MailToButton
                href={mailtoLink}
                title={t('apply-as-speaker')}
                backgroundColor={sluggedEvent?.design?.themeColor}
                hoverBackgroundColor={theme.color.mobilityGrey}
                textColor={theme.color.white}
                borderRadius={theme.borderRadius.radius10}
                fontSize={theme.fontSize.size20}
                lineHeight={theme.lineHeight.height150}
                padding={isMobile ? '1.5rem 2.5rem' : '1.1rem 4.9rem'}
                width="fit-content"
              />
            }
          />
        </GridSpan>
        <GridSpan span={12}>
          <AttendeeInfo speaker={speakerDetails?.data} />
        </GridSpan>
        <GridSpan span={12}>
          <Sessions sessions={speakerDetails?.data?.sessions} />
          {speakerDetails?.data?.company && (
            <CompanyInfo company={speakerDetails.data.company} />
          )}
        </GridSpan>
      </SectionGrid>
    </>
  );
};

export default SpeakerPage;
